import { Stack, Tooltip, Typography } from "@mui/material";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { IEdition, LearningActivity, ReferencedLearningActivity } from "types";
import { AgendaList } from "./AgendaList.component";
import { Header } from "./Header.component";
import { ContentContainer, EmptyStateText } from "./LearningActivity.styles";
import { LearningActivityContentSkeleton } from "./LearningActivityContentSkeleton.component";
import { getFeedbackDescription, getFeedbackText, useEditions } from "./useEditions.hook";
import { UtilityTabs } from "./UtiliyTabs.component";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { AvailableLanguages } from "../../components/Commons/AvailableLanguages.component";
import { Favourite } from "../../components/Commons/Favourite.component";
import { CtaOverlay } from "../../components/CtaOverlay";
import { useDescendantClassrooms } from "../../components/DetailsEsploraRestyle/DescendantsClassrooms.context";
import { useModal } from "../../components/Modal/useModal.hook";
import {
  getEditionJoinAvailability,
  getEditionMeetingUrl,
  isEnrolledLoStatus
} from "../../components/TabsContent/Editions/utils";
import { LEVEL } from "../../consts";
import {  useLearningObjectQuery, useToolsQuery } from "../../queries";
import { useGetExercisesList } from "../../queries/exercises";
import { getError } from "../../utils/error";
import { useRouteParamsLo } from "../../utils/useRouteParamsLo";

type ILearningActivityContent = {
  activity: ReferencedLearningActivity
  handleClose: (e: React.MouseEvent<HTMLButtonElement>) => void
  initialEditions?: IEdition[]
}

export const LearningActivityVirtualClassroomContent = (({
  activity,
  handleClose,
  initialEditions
}: ILearningActivityContent) => {

  const { t } = useTranslation();
  const { id, courseId, learningObjectTypology, rootId } = activity;
  const {
    rootIdNumber
  } = useRouteParamsLo();

  const {
    data: learningObject,
    error: learningObjectError,
    isFetching: learningObjectIsFetching
  } = useLearningObjectQuery({
    rootId: rootId,
    courseId: courseId,
    id: id ?? "",
    learningObjectTypology: learningObjectTypology
  });

  const { renderModal, setModalText, setModalDescription, setShowModal, setIconType } = useModal();

  const { approvalGranted } = useDescendantClassrooms();

  const editionsQueryData = useEditions(
    learningObject as LearningActivity,
    initialEditions,
    approvalGranted,
    { courseId, rootId }
  );

  // error feedback from editions query API
  const { feedback, clearFeedback, editions, editionsIsFetching } = editionsQueryData;

  useEffect(() => {
    if (!feedback) return;

    setModalText(getFeedbackText(feedback, t));
    setModalDescription(getFeedbackDescription(feedback, t));
    setIconType(feedback.iconType);
    setShowModal(true);
    clearFeedback();
  }, [feedback, clearFeedback, setModalText, setModalDescription, setShowModal, setIconType, t]);

  const { data: tools, isFetching: toolsIsFetching } = useToolsQuery({
    learningObjectId: learningObject?.id?.toString() ?? ""
  });

  const editionBooked = editions?.find(edition => edition.status === "E");

  const { data: exercises, isFetching: exercisesIsFetching } = useGetExercisesList({ editionId: editionBooked?.id });


  const isVirtualClassroom = learningObject?.learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS;
  const isClassroom = isVirtualClassroom
    || learningObject?.learningObjectTypology === LEVEL.DA_PHYSICAL_CLASS;
  // Le edizioni concluse ("C") restano incluse: il CTA va mostrato disabilitato, non nascosto.
  const enrolledEdition = editions?.find(edition => isEnrolledLoStatus(edition.status) && !edition.isAbsent);
  const joinAvailability = isClassroom && enrolledEdition
    ? getEditionJoinAvailability(enrolledEdition, isVirtualClassroom)
    : "not_yet";
  const joinUrl = enrolledEdition ? getEditionMeetingUrl(enrolledEdition) : null;
  const showJoinButton = isClassroom && !!enrolledEdition && (isVirtualClassroom || !!joinUrl);
  const canJoinClassroom = joinAvailability === "available" && !!joinUrl;
  const joinTooltipKey = joinAvailability === "ended" ? "join_no_longer_available" : "join_not_available_yet";
  const handleJoinClassroom = () => {
    if (!joinUrl) return;
    window.open(joinUrl, "_blank", "noreferrer");
  };

  const isContentLoading = learningObjectIsFetching || toolsIsFetching || exercisesIsFetching || editionsIsFetching;

  if (!learningObjectIsFetching && !learningObject) {
    const errorMessage = getError(learningObjectError)?.[0]?.message
      ?? t("no_data_available");
    return (
      <ContentContainer>
        <Header activity={ { ...activity } as unknown as LearningActivity } handleClose={ handleClose } />
        <EmptyStateText role="alert">{ errorMessage }</EmptyStateText>
      </ContentContainer>
    );
  }

  if (isContentLoading) {
    const headerActivity = (learningObject as LearningActivity | undefined)
      ?? ({ ...activity } as unknown as LearningActivity);
    return (
      <ContentContainer>
        <Header activity={ headerActivity } handleClose={ handleClose } />
        <LearningActivityContentSkeleton />
        { renderModal(sxStyle) }
      </ContentContainer>
    );
  }

  const {
    isStandAlone,
    isFavourite,
    averageRatingScore,
    certificates,
    badge
  } = learningObject as LearningActivity;

  const agenda = (learningObject as LearningActivity)?.agenda;

  return (
    <ContentContainer>
      <Header
        activity={ learningObject as LearningActivity }
        handleClose={ handleClose }
      />

      { /* description activity & domains */ }
      <Stack>
        <Typography
          fontSize={ "0.875rem" }
          letterSpacing={ "0.0156rem" }
        >
          { learningObject?.longDescription ?? learningObject?.shortDescription ?? "" }
        </Typography>
        <AvailableLanguages
          availableLanguages={ learningObject?.availableLanguages }
          component={ Typography }
          sx={ { fontSize: "0.875rem", letterSpacing: "0.0156rem", marginTop: "1rem" } }
        />
        { /* <DomainsList /> */ }
      </Stack>

      { /* programma / agendas */ }
      { agenda?.length > 0 && (
        <Stack>
          <AgendaList agenda={ (learningObject as LearningActivity)?.agenda } />
        </Stack>
      ) }

      { /* CTA */ }
      <Stack
        flexDirection={ "row" }
        justifyContent={ "space-between" }
        position={ "relative" }
        sx={ {
          "& #cta-overlay-toggle": {
            position: "unset"
          }
        } }
      >
        <Stack flexDirection={ "row" } alignItems={ "center" } gap={ "0.5rem" }>
          { showJoinButton && (
            <Tooltip title={ canJoinClassroom ? "" : t(joinTooltipKey) }>
              <span style={ { display: "inline-flex" } }>
                <ButtonDigited
                  id={ `modal-join-button-${id}` }
                  data-testid="modal-join-button"
                  aria-label={ t("join") }
                  typology="primary"
                  disabled={ !canJoinClassroom }
                  onClick={ handleJoinClassroom }
                >
                  { t("join") }
                </ButtonDigited>
              </span>
            </Tooltip>
          ) }
          { isStandAlone && !courseId && !rootId && (
            <Favourite
              isStandAlone={ isStandAlone }
              id={ id }
              rootId={ rootIdNumber }
              courseId={ courseId }
              learningObjectTypology={ learningObjectTypology }
              isFavourite={ isFavourite }
            />
          ) }
        </Stack>
        <CtaOverlay
          data={ {
            isStandAlone: isStandAlone,
            isFavourite: isFavourite,
            id: id,
            learningObjectTypology: learningObjectTypology,
            rating: averageRatingScore ?? 0,
            courseId: courseId,
            rootId: rootIdNumber
          } }
          certificates={ certificates }
          badge={ badge }
          hideShare
          iconOnly={ true }
          sxContainer={ {
            position: "unset"
          } }
          hideRating
          keepIconsInMobile={ true }
        />
      </Stack>

      <UtilityTabs
        learningObject={ learningObject as LearningActivity }
        tools={ tools ?? [] }
        exercises={ exercises ?? [] }
        editionsQueryData={ editionsQueryData }
      />
      { renderModal(sxStyle) }
    </ContentContainer>
  );
});

const sxStyle = ()=> ({
  width: "31rem",
  ".MuiTypography-root":{
    maxWidth:"unset",
    textAlign:"center"
  }
});
